import { useContext } from 'react';
import { Alert, Button, Empty, Table as TableAntd, Tag } from 'antd';
import { 
    ClockCircleOutlined, 
} from '@ant-design/icons';
import TableCard from '../../../../../components/TableCard';
import TableActionButton from '../../../../../components/TableActionButton';
import { IInvoicesTotal } from '../../../../../services/http/services/InvoicesService';
import { convertMoedaBRDoJSNovo, convertMonthToString } from "../../../../../utils/Conversores";
import { ThemeContext } from '../../../../../contexts/ThemeContext';

interface IProps {
    totalProcessingInvoices: number;
    invoices: IInvoicesTotal[];
    loadInvoicesByPeriod: ({ month, year }: { month: number, year: number }) => void;
    handleShowModalInvoice: () => void;
    handleClickViewInvoicesInProcessing: () => void;
}

export default function TabInvoices({
    totalProcessingInvoices,
    invoices,
    loadInvoicesByPeriod,
    handleShowModalInvoice,
    handleClickViewInvoicesInProcessing
}: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';

    const columns = [
        {
            title: 'Período',
            key: 'period',
            sorter: (a: IInvoicesTotal, b: IInvoicesTotal) => (a.year * 100 + a.month) - (b.year * 100 + b.month),
            defaultSortOrder: 'descend' as const,
            render: (_: any, record: IInvoicesTotal) => (
                <span style={{ fontWeight: 600, color: isDark ? '#f0f0f0' : '#1e293b' }}>
                    {convertMonthToString(record.month)} / {record.year}
                </span>
            ),
        },
        {
            title: 'Valor Total',
            dataIndex: 'total_value',
            key: 'total_value',
            align: 'right' as const,
            sorter: (a: IInvoicesTotal, b: IInvoicesTotal) => (a.total_value || 0) - (b.total_value || 0),
            render: (value: number) => (
                <span style={{ fontWeight: 600, color: isDark ? '#52c41a' : '#389e0d', whiteSpace: 'nowrap' }}>
                    R$ {convertMoedaBRDoJSNovo(value)}
                </span>
            ),
        },
        {
            title: 'Válidas',
            dataIndex: 'completed_invoices',
            key: 'completed_invoices',
            align: 'center' as const,
            sorter: (a: IInvoicesTotal, b: IInvoicesTotal) => (a.completed_invoices || 0) - (b.completed_invoices || 0),
            render: (val: number) => (
                <Tag color="success" style={{ margin: 0, borderRadius: 4, fontWeight: 600 }}>
                    {val || 0}
                </Tag>
            ),
        },
        {
            title: 'Com Erro',
            dataIndex: 'other_invoices',
            key: 'other_invoices',
            align: 'center' as const,
            sorter: (a: IInvoicesTotal, b: IInvoicesTotal) => (a.other_invoices || 0) - (b.other_invoices || 0),
            render: (val: number) => (
                val > 0 ? (
                    <Tag color="error" style={{ margin: 0, borderRadius: 4, fontWeight: 600 }}>
                        {val}
                    </Tag>
                ) : (
                    <span style={{ color: isDark ? '#666' : '#94a3b8' }}>0</span>
                )
            ),
        },
        {
            title: 'Total Notas',
            key: 'total_invoices',
            align: 'center' as const,
            sorter: (a: IInvoicesTotal, b: IInvoicesTotal) => 
                (a.total_invoices || (a.completed_invoices + (a.other_invoices || 0) + (a.warning_invoices || 0))) - 
                (b.total_invoices || (b.completed_invoices + (b.other_invoices || 0) + (b.warning_invoices || 0))),
            render: (_: any, record: IInvoicesTotal) => (
                <span style={{ fontWeight: 600 }}>
                    {record.total_invoices || (record.completed_invoices + (record.other_invoices || 0) + (record.warning_invoices || 0))}
                </span>
            ),
        },
        {
            title: 'Ações',
            key: 'actions',
            align: 'center' as const,
            width: '8%',
            render: (_: any, record: IInvoicesTotal) => (
                <TableActionButton
                    action="view"
                    tooltip="Visualizar notas fiscais deste período"
                    onClick={() => {
                        loadInvoicesByPeriod({ month: record.month, year: record.year });
                        handleShowModalInvoice();
                    }}
                />
            ),
        },
    ];

    return (
        <div style={{ paddingTop: 4 }}>
            {/* Processing Invoices Alert */}
            {totalProcessingInvoices > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    icon={<ClockCircleOutlined />}
                    message={
                        <span>
                            <strong>{totalProcessingInvoices}</strong> {totalProcessingInvoices === 1 ? 'nota fiscal em processamento' : 'notas fiscais em processamento'}
                        </span>
                    }
                    description="Existem notas fiscais aguardando processamento pelo sistema."
                    action={
                        <Button
                            size="small"
                            type="primary"
                            ghost
                            onClick={handleClickViewInvoicesInProcessing}
                            style={{ borderRadius: 6, fontWeight: 500 }}
                        >
                            Ver notas
                        </Button>
                    }
                    style={{
                        marginBottom: 14,
                        borderRadius: 10,
                    }}
                />
            )}

            {/* Invoices Table */}
            <TableCard>
                <TableAntd
                    className={`modern-data-table ${isDark ? 'modern-data-table-dark' : ''}`}
                    columns={columns}
                    dataSource={invoices.map((inv, idx) => ({
                        ...inv,
                        key: `${inv.year}-${inv.month}-${idx}`,
                    }))}
                    onRow={(record) => ({
                        onClick: () => {
                            loadInvoicesByPeriod({ month: record.month, year: record.year });
                            handleShowModalInvoice();
                        },
                        style: {
                            cursor: 'pointer',
                        },
                    })}
                    locale={{
                        emptyText: (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    <span style={{ color: isDark ? '#a6a6a6' : '#64748b' }}>
                                        Usuário não possui notas neste município
                                    </span>
                                }
                            />
                        ),
                    }}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50'],
                    }}
                />
            </TableCard>
        </div>
    );
}



